import { ReactNode, useEffect, useRef, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Card } from "primereact/card";

import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { FullScreenLoader } from "sj-ui-toolkit/src/components";

// Give the lock this long to arrive before telling the user another lesson tab is open.
// Covers the brief hand-off when one lesson route unmounts and another mounts in the same tab.
const BLOCKED_MESSAGE_DELAY_MS = 300;

const BLOCKED_TITLE = "Lesson open in another tab";
const BLOCKED_MESSAGE =
  "Close your other lesson tab for the best experience. This lesson will load automatically once it is closed.";

type LockState = "pending" | "blocked" | "acquired";

/**
 * Limits a signed-in user to one running lesson per browser (`limit-one-lesson-per-user`).
 *
 * A Lock for the lifetime of the lesson route. A second tab opening a lesson waits
 * on the same lock, shows a "close your other tab" message, and renders the lesson on its own
 * as soon as the first tab closes or navigates away (the browser releases the lock for us).
 *
 * Skipped when the flag is off, there is no signed-in user (guest access), or the browser has
 * no Web Locks support.
 */
export default function SingleLessonGuard({ children }: { children: ReactNode }) {
  const { limitOneLessonPerUser } = useFlags();
  const { user } = useUserContext();
  const userId = user?.userId;
  const enabled = !!limitOneLessonPerUser && !!userId && "locks" in navigator;

  const [lockState, setLockState] = useState<LockState>("pending");
  const hasRenderedChildren = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();
    let release: (() => void) | undefined;
    const blockedTimer = window.setTimeout(() => setLockState("blocked"), BLOCKED_MESSAGE_DELAY_MS);

    navigator.locks
      .request(`sj-lesson:${userId}`, { signal: controller.signal }, () => {
        window.clearTimeout(blockedTimer);
        setLockState("acquired");
        // Hold the lock until this route unmounts
        return new Promise<void>(resolve => {
          release = resolve;
        });
      })
      .catch(() => {
        // Aborted on unmount while still waiting for the lock
      });

    return () => {
      window.clearTimeout(blockedTimer);
      controller.abort();
      release?.();
    };
  }, [enabled, userId]);

  const showChildren = !enabled || lockState === "acquired" || hasRenderedChildren.current;

  useEffect(() => {
    if (showChildren) hasRenderedChildren.current = true;
  }, [showChildren]);

  if (showChildren) {
    return <>{children}</>;
  }

  if (lockState === "blocked") {
    return (
      <div className="flex items-center justify-center h-full p-8" role="status" aria-live="polite">
        <Card className="w-full max-w-md">
          <div className="flex flex-col items-center gap-6 p-4 text-center">
            <div className="flex items-center justify-center size-15 rounded-full bg-blue-100 text-blue-600 text-3xl">
              <i className="pi pi-clone" aria-hidden="true" />
            </div>
            <h2 className="atlas-h4 m-0">{BLOCKED_TITLE}</h2>
            <p className="text-muted m-0">{BLOCKED_MESSAGE}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full">
      <FullScreenLoader />
    </div>
  );
}
